import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const CANVAS_SOURCE = 'https://www.w3.org/TR/html52/canvas.html';

/**
 * 2D canvas image fingerprint.
 *
 * The classic fingerprinting surface, and still a real differentiator: canvas
 * rendering differs by GPU, driver, font stack, and anti-aliasing. Anti-detect
 * browsers counter it by adding per-call pixel noise, so we deliberately render
 * the SAME scene twice within one scan. A native browser produces identical
 * bytes; a noise-injecting anti-detect browser does NOT, because the noise is
 * regenerated per call. That stability check is worth more than the hash itself.
 *
 * The scene mixes text, gradients, arcs and shapes so the rasterizer's
 * rounding behaviour is exercised across several code paths.
 */
export const canvasProbe: Probe = {
  id: 'canvas',
  label: 'Canvas 2D',
  layer: 'gpu',
  tier: 'client',
  category: 'hardware',
  description: '2D canvas image fingerprint plus per-call stability check',
  sources: [CANVAS_SOURCE],
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    const ctx = createContext();
    if (!ctx) {
      return { signals: { available: na<boolean>('unavailable', 'client', 'no 2d context') } };
    }

    const dataUrlA = renderScene(ctx);
    const dataUrlB = renderScene(ctx);

    if (!dataUrlA) {
      return {
        signals: {
          available: na<boolean>('unavailable', 'client', 'toDataURL returned empty'),
        },
      };
    }

    const stable = dataUrlA === dataUrlB;
    const hash = await fnv1aHex(dataUrlA);

    // Pixel-level stats are cheaper to diff across two runs than the PNG bytes
    // and are what most comparison tooling shows.
    const stats = pixelStats(ctx);

    return {
      signals: {
        available: ok(true, 'client'),
        hash: ok(hash, 'client'),
        // The anti-detect tell: real browsers are byte-stable across calls.
        renderStableAcrossCalls: ok(stable, 'derived'),
        dataUrlLength: ok(dataUrlA.length, 'client'),
        nonBackgroundPixels: ok(stats.nonBackground, 'client'),
        meanChannel: ok(sig(stats.meanChannel, 3), 'client'),
        channelSpread: ok(sig(stats.channelSpread, 3), 'client'),
      },
      parts: {
        gpu: {
          // Hash the stable canvas hash. If rendering is NOT stable across
          // calls (an anti-detect tool injecting noise), hash the marker
          // instead of a value that would differ on every single scan.
          canvasHash: stable ? hash : 'unstable',
        },
      },
    };
  },
};

function createContext(): CanvasRenderingContext2D | null {
  const canvas = document.createElement('canvas');
  canvas.width = 300;
  canvas.height = 60;
  // willReadFrequently: we call getImageData on this context, and Chromium
  // warns in the console without it. It also makes the readback path genuinely
  // faster, which is worth having for a probe that hashes pixels.
  return canvas.getContext('2d', { willReadFrequently: true });
}

/** Renders a mixed scene and returns its data URL. */
function renderScene(ctx: CanvasRenderingContext2D): string | null {
  try {
    const { width: w, height: h } = ctx.canvas;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = '#069';
    ctx.font = '11pt Arial';
    ctx.fillText('Cwm fjordbank glyphs vext quiz 😃', 2, 15);

    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.font = '18pt Arial';
    ctx.fillText('Cwm fjordbank glyphs vext quiz 😃', 4, 45);

    ctx.globalCompositeOperation = 'multiply';
    const grd = ctx.createLinearGradient(0, 0, w, h);
    grd.addColorStop(0, 'rgb(255,0,255)');
    grd.addColorStop(1, 'rgb(0,255,255)');
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, w, h);

    ctx.globalCompositeOperation = 'source-over';
    ctx.beginPath();
    ctx.arc(250, 30, 20, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fillStyle = '#f60';
    ctx.fill();

    return ctx.canvas.toDataURL();
  } catch {
    return null;
  }
}

function pixelStats(ctx: CanvasRenderingContext2D): {
  nonBackground: number;
  meanChannel: number;
  channelSpread: number;
} {
  try {
    const { width: w, height: h } = ctx.canvas;
    const data = ctx.getImageData(0, 0, w, h).data;
    let sum = 0;
    let min = 255;
    let max = 0;
    // The top-left pixel is the solid #f60 fill we start with; count everything
    // that differs from it as "drawn".
    const bg = [0xf6, 0x00, 0x60];
    let nonBackground = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const luma = (r + g + b) / 3;
      sum += luma;
      if (luma < min) min = luma;
      if (luma > max) max = luma;
      if (Math.abs(r - bg[0]) > 2 || Math.abs(g - bg[1]) > 2 || Math.abs(b - bg[2]) > 2) {
        nonBackground++;
      }
    }
    const n = data.length / 4;
    return {
      nonBackground,
      meanChannel: n ? sum / n : 0,
      channelSpread: max - min,
    };
  } catch {
    // Tainted canvas (should not happen here, but be defensive).
    return { nonBackground: 0, meanChannel: 0, channelSpread: 0 };
  }
}

/** FNV-1a, 64-bit-ish: matches the style of the other reference-site hashes. */
async function fnv1aHex(input: string): Promise<string> {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 = (Math.imul(h2 ^ c, 0x85ebca6b) >>> 0) ^ (h2 >>> 13);
  }
  return (
    h1.toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0')
  ).slice(0, 16);
}

registerProbe(canvasProbe);
